import React, { useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import FollowUpHistoryPanel from "./FollowUpHistoryPanel";
import NodeHistoryModal from "./NodeHistoryModal";

// =============================================================
// MemberDetailPanel — slides in from the right on desktop and up
// from the bottom on mobile. Shows the tapped node's member,
// their leader (parent) and 3 direct reports, plus Actions for SA.
// =============================================================
export default function MemberDetailPanel({
  node, tree, members, currentUser,
  isSuperAdmin, isLimitedAdmin,
  onClose, onNavigateNode, onOpenAssign, onOpenReassign, onOpenRemove, onOpenViewProfile,
}) {
  const [historyKind, setHistoryKind] = useState(null); // "slot" | "member" | null
  if (!node) return null;
  // Special God panel — read-only, no actions.
  if (node.isGodNode) {
    return (
      <div
        data-testid="member-detail-panel"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      >
        <div
          data-testid="god-detail-panel"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", borderRadius: 16,
            width: "min(420px, 100%)", padding: "32px 24px calc(28px + env(safe-area-inset-bottom)) 24px",
            boxShadow: "0 24px 60px rgba(0,0,0,0.5), 0 0 40px color-mix(in srgb, var(--kc-accent) 20%, transparent)",
            textAlign: "center", position: "relative",
          }}
        >
          <button
            type="button"
            data-testid="god-detail-close"
            onClick={onClose}
            aria-label="Close"
            style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", color: "var(--kc-sub)", cursor: "pointer", fontSize: 20 }}
          >×</button>
          <div style={{
            width: 128, height: 128, margin: "8px auto 20px",
            borderRadius: "50%",
            background: "radial-gradient(circle at 30% 30%, #F5D77A, #B8860B 65%, #6b4a05)",
            border: "3px solid var(--kc-accent)",
            boxShadow: "0 0 32px color-mix(in srgb, var(--kc-accent) 75%, transparent)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "godGlowPulseLarge 3.6s ease-in-out infinite",
          }}>
            <CustomIcon name="crown" size={72} />
          </div>
          <style>{`
            @keyframes godGlowPulseLarge {
              0%,100% { box-shadow: 0 0 24px color-mix(in srgb, var(--kc-accent) 55%, transparent); }
              50%    { box-shadow: 0 0 40px color-mix(in srgb, var(--kc-accent) 95%, transparent); }
            }
          `}</style>
          <div data-testid="god-title" style={{
            fontFamily: "'Cinzel',serif", fontSize: 30, fontWeight: 700,
            letterSpacing: "0.14em", color: "var(--kc-accent)", textTransform: "uppercase",
          }}>
            {node.title || "God"}
          </div>
          <div data-testid="god-level" style={{
            marginTop: 6,
            fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700,
            letterSpacing: "0.12em", color: "var(--kc-accent)", textTransform: "uppercase",
          }}>
            Level {node.levelName || "Alpha"} · {node.slotRef || "AL-001"}
          </div>
          <div style={{
            width: 60, height: 1, background: "color-mix(in srgb, var(--kc-accent) 55%, transparent)", margin: "14px auto",
          }} />
          <div data-testid="god-identity" style={{
            fontFamily: "'Montserrat',sans-serif", fontStyle: "italic",
            fontSize: 14, color: "var(--kc-text)", letterSpacing: "0.02em",
          }}>
            {node.identity || "Father, Son and Holy Spirit"}
          </div>
        </div>
      </div>
    );
  }
  const nodes = tree?.nodes || {};
  const member = node.memberId ? (members || []).find(m => m.id === node.memberId) : null;
  const parent = node.parentNodeId ? nodes[node.parentNodeId] : null;
  const parentMember = parent?.memberId ? (members || []).find(m => m.id === parent.memberId) : null;
  const children = (node.childNodeIds || []).map(id => nodes[id]).filter(Boolean);
  const isVacant = !member;
  const canManage = isSuperAdmin;

  const displayName = member
    ? ((member.firstName || "") + " " + (member.lastName || "")).trim()
    : "Vacant Slot";

  const followUpBadge = (() => {
    if (!member) return null;
    const s = (member.status || "Active").toLowerCase();
    let color = "#4ade80";
    if (s === "inactive" || s === "transferred") color = "rgba(200,200,200,0.55)";
    if (s === "removed") color = "#ff6b6b";
    if (s === "visitor") color = "#f59e0b";
    if (s === "deceased") color = "#a5b4fc";
    return { label: (member.status || "Active"), color };
  })();

  return (
    <div
      data-testid="member-detail-panel"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
        display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
      }}
    >
      <div
        data-testid="member-detail-inner"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--kc-surface)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
          width: "min(420px, 100%)",
          height: "100vh",
          maxHeight: "100vh",
          padding: "20px 18px calc(24px + env(safe-area-inset-bottom)) 18px",
          overflowY: "auto",
          boxShadow: "-12px 0 30px rgba(0,0,0,0.4)",
        }}
        className="member-detail-inner mobile-safe-bottom-pad"
      >
        <style>{`
          @media (max-width: 768px) {
            [data-testid="member-detail-panel"] { align-items: flex-end !important; justify-content: center !important; }
            [data-testid="member-detail-panel"] .member-detail-inner {
              width: 100% !important;
              max-height: 85vh !important;
              height: auto !important;
              border-top-left-radius: 20px;
              border-top-right-radius: 20px;
              box-shadow: 0 -12px 30px rgba(0,0,0,0.4) !important;
            }
          }
        `}</style>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, letterSpacing: "0.10em", color: "var(--kc-accent)", textTransform: "uppercase", fontWeight: 700 }}>
              {node.levelName} · #{node.memberNumber || "—"}
            </div>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>
              Slot <strong style={{ color: "var(--kc-text)" }}>{node.slotRef}</strong>
            </div>
          </div>
          <button data-testid="member-detail-close" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", color: "var(--kc-accent)", borderRadius: 999, width: 32, height: 32, cursor: "pointer", fontSize: 15 }}>×</button>
        </div>

        {/* Avatar + name */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 18 }}>
          {isVacant ? (
            <div style={{ width: 80, height: 80, borderRadius: "50%", border: "2px dashed color-mix(in srgb, var(--kc-accent) 55%, transparent)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kc-accent)" }}>
              <CustomIcon name="plus" size={28} />
            </div>
          ) : (
            <Avatar
              name={displayName}
              photo={member?.photo}
              memberId={member?.id}
              status={member?.memberStatus}
              roleOverride={currentUser?.role}
              style={{ width: 80, height: 80, fontSize: 24, fontWeight: 700 }}
              data-testid="member-detail-avatar"
            />
          )}
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 18, fontWeight: 700, color: "var(--kc-text)", textAlign: "center", textTransform: "uppercase", letterSpacing: "0.05em" }} data-testid="member-detail-name">
            {displayName}
          </div>
          {followUpBadge && (
            <span data-testid="member-detail-status" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 999, color: followUpBadge.color, border: `1px solid ${followUpBadge.color}`, background: "rgba(255,255,255,0.02)" }}>
              {followUpBadge.label}
            </span>
          )}
        </div>

        {/* Vacant history */}
        {isVacant && (
          <>
            {(node.occupancyLog || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Occupancy history</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {(node.occupancyLog || []).map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: "var(--kc-sub)" }}>
                      #{e.memberNumber} — {e.memberName} <span style={{ color: "var(--kc-meta)" }}>({(e.dateAssigned || "").slice(0, 10)} → {(e.dateDeparted || "active").slice(0, 10)})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {canManage && (
              <button
                type="button"
                className="btn btn-gold btn-sm"
                data-testid="member-detail-assign"
                onClick={() => onOpenAssign && onOpenAssign(node)}
                style={{ width: "100%" }}
              >Assign Member</button>
            )}
          </>
        )}

        {/* Leader (parent) — visible even for vacant nodes so admins can jump up */}
        {parent && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Leader (one level above)</div>
            <button
              type="button"
              data-testid="member-detail-leader"
              onClick={() => onNavigateNode && onNavigateNode(parent.nodeId)}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 10px", background: "color-mix(in srgb, var(--kc-accent) 5%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, cursor: "pointer", color: "inherit", textAlign: "left", font: "inherit" }}
            >
              {parentMember ? (
                <Avatar
                  name={`${parentMember.firstName || ""} ${parentMember.lastName || ""}`.trim()}
                  photo={parentMember.photo}
                  memberId={parentMember.id}
                  status={parentMember.memberStatus}
                  roleOverride={currentUser?.role}
                  style={{ width: 32, height: 32, fontSize: 12 }}
                />
              ) : (
                <div style={{ width: 32, height: 32, borderRadius: "50%", border: "1px dashed color-mix(in srgb, var(--kc-accent) 50%, transparent)" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kc-text)" }}>
                  {parentMember ? `${parentMember.firstName || ""} ${parentMember.lastName || ""}`.trim() : "Vacant"}
                </div>
                <div style={{ fontSize: 11, color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>
                  {parent.slotRef} · {parent.levelName}
                </div>
              </div>
              <CustomIcon name="arrow-right" size={12} />
            </button>
          </div>
        )}

        {/* Direct reports */}
        {!isVacant && children.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Direct reports ({children.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {children.map(c => {
                const m = c.memberId ? (members || []).find(x => x.id === c.memberId) : null;
                return (
                  <button
                    key={c.nodeId}
                    type="button"
                    data-testid={`member-detail-child-${c.slotRef}`}
                    onClick={() => onNavigateNode && onNavigateNode(c.nodeId)}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "6px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, cursor: "pointer", color: "inherit", textAlign: "left", font: "inherit" }}
                  >
                    {m ? (
                      <Avatar
                        name={`${m.firstName || ""} ${m.lastName || ""}`.trim()}
                        photo={m.photo}
                        memberId={m.id}
                        status={m.memberStatus}
                        roleOverride={currentUser?.role}
                        style={{ width: 26, height: 26, fontSize: 10 }}
                      />
                    ) : (
                      <div style={{ width: 26, height: 26, borderRadius: "50%", border: "1px dashed color-mix(in srgb, var(--kc-accent) 50%, transparent)" }} />
                    )}
                    <span style={{ flex: 1, fontSize: 12, color: "var(--kc-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : "Vacant slot"} <span style={{ color: "var(--kc-accent)" }}>{c.slotRef}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* View Profile */}
        {!isVacant && (
          <button
            type="button"
            className="btn btn-gold"
            data-testid="member-detail-view-profile"
            onClick={() => onOpenViewProfile && member && onOpenViewProfile(member)}
            style={{ width: "100%", marginBottom: 12, justifyContent: "center", display: "flex", alignItems: "center", gap: 6 }}
          ><CustomIcon name="person" size={12} /> View Profile</button>
        )}

        {/* Admin actions */}
        {!isVacant && canManage && (
          <div style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", paddingTop: 12 }}>
            <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>Actions</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-sm btn-outline" data-testid="member-detail-reassign" onClick={() => onOpenReassign && onOpenReassign(node)} style={{ flex: 1 }}>Reassign</button>
              <button className="btn btn-sm" data-testid="member-detail-remove" onClick={() => onOpenRemove && onOpenRemove(node)} style={{ flex: 1, background: "#ff6b6b", color: "#111", border: "1px solid #ff6b6b" }}>Remove</button>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="btn btn-sm btn-outline" data-testid="member-detail-slot-history" onClick={() => setHistoryKind("slot")} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <CustomIcon name="home" size={12} /> Slot History
              </button>
              <button className="btn btn-sm btn-outline" data-testid="member-detail-member-history" onClick={() => setHistoryKind("member")} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <CustomIcon name="person" size={12} /> Member History
              </button>
            </div>
          </div>
        )}
        {/* Vacant slot Super Admin history lookup */}
        {isVacant && canManage && (
          <div style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", paddingTop: 12, marginTop: 8 }}>
            <button className="btn btn-sm btn-outline" data-testid="member-detail-slot-history" onClick={() => setHistoryKind("slot")} style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <CustomIcon name="home" size={12} /> Slot History
            </button>
          </div>
        )}
        {!isVacant && !canManage && isLimitedAdmin && (
          <div style={{ fontSize: 11, color: "var(--kc-meta)", fontStyle: "italic", marginTop: 6 }}>
            Limited Admin — read-only access.
          </div>
        )}

        {/* Follow-Up History — visible for occupied nodes */}
        {!isVacant && <FollowUpHistoryPanel node={node} />}
      </div>
      {historyKind && (
        <NodeHistoryModal
          kind={historyKind}
          node={node}
          member={member}
          onClose={() => setHistoryKind(null)}
        />
      )}
    </div>
  );
}
